import React, { useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { X as XIcon, ExternalLink } from 'lucide-react';

const backdropFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 18, 41, 0.3);
  z-index: 1100;
  animation: ${backdropFadeIn} 0.2s ease-out;
`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: ${props => props.$width || '420px'};
  height: 100vh;
  background-color: ${props => props.theme.colors.background};
  border-left: 1px solid ${props => props.theme.colors.border};
  box-shadow: -8px 0 32px rgba(35, 37, 98, 0.18);
  z-index: 1101;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 480px) {
    width: 100vw;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  flex-shrink: 0;
`;

const PanelTitle = styled.h2`
  font-size: ${props => props.theme.typography.sizes.medium};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-shrink: 0;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-size: 16px;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.theme.colors.tint};
  }
`;

const OpenFullLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: none;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.theme.colors.tint}12;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.lg};

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 4px;
  }
`;

export const SlidePanel = ({ isOpen, onClose, title, width, onOpenFull, children, theme }) => {
  // Close on ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <Backdrop onClick={onClose} />
      <Panel theme={theme} $width={width}>
        <PanelHeader theme={theme}>
          <PanelTitle theme={theme}>{title}</PanelTitle>
          <HeaderActions theme={theme}>
            {onOpenFull && (
              <OpenFullLink theme={theme} onClick={onOpenFull}>
                Open Full <ExternalLink size={12} style={{ marginLeft: 2 }} />
              </OpenFullLink>
            )}
            <IconButton theme={theme} onClick={onClose} title="Close">
              <XIcon size={16} />
            </IconButton>
          </HeaderActions>
        </PanelHeader>
        <PanelBody theme={theme}>
          {children}
        </PanelBody>
      </Panel>
    </>
  );
};
